(function () {
  if (!window.customElements || customElements.get('product-drawers')) return;

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  class ProductDrawers extends HTMLElement {
    connectedCallback() {
      this.scrim = this.querySelector('[data-drawer-scrim]');
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-drawer]'));
      this.opener = null;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeydown);

      if (this.scrim) this.scrim.addEventListener('click', this.onClick);

      this.checkButton = this.querySelector('[data-postcode-check]');
      this.postcodeInput = this.querySelector('[data-postcode-input]');
      this.postcodeResult = this.querySelector('[data-postcode-result]');

      if (this.checkButton) {
        this.onCheck = this.onCheck.bind(this);
        this.checkButton.addEventListener('click', this.onCheck);
      }

      this.updateDeliveryDate();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeydown);
      if (this.scrim) this.scrim.removeEventListener('click', this.onClick);
      if (this.checkButton) this.checkButton.removeEventListener('click', this.onCheck);
      this.unlockScroll();
    }

    get openPanel() {
      return this.panels.filter(function (panel) {
        return !panel.hidden;
      })[0];
    }

    onClick(event) {
      const trigger = event.target.closest ? event.target.closest('[data-drawer-open]') : null;
      if (trigger) {
        event.preventDefault();
        this.opener = trigger;
        this.open(trigger.getAttribute('data-drawer-open'));
        return;
      }

      if (event.target.closest && event.target.closest('[data-drawer-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.currentTarget === this.scrim) this.close();
    }

    onKeydown(event) {
      const panel = this.openPanel;
      if (!panel) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const items = Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0;
      });
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open(id) {
      const panel = this.querySelector('[data-drawer="' + id + '"]');
      if (!panel) return;

      this.panels.forEach(function (other) {
        if (other !== panel) {
          other.hidden = true;
          other.classList.remove('is-open');
        }
      });

      panel.hidden = false;
      this.classList.add('is-open');
      this.lockScroll();

      requestAnimationFrame(() => {
        panel.classList.add('is-open');
        const focusTarget = panel.querySelector(FOCUSABLE);
        if (focusTarget) focusTarget.focus();
      });
    }

    close() {
      const panel = this.openPanel;
      if (!panel) return;

      panel.classList.remove('is-open');
      panel.hidden = true;
      this.classList.remove('is-open');
      this.unlockScroll();

      if (this.opener && document.contains(this.opener)) {
        this.opener.focus();
        this.opener = null;
      }
    }

    /*
     * The page is cached, so the server-rendered delivery date can be a day old. Recompute it
     * here and write it in the page's own language, e.g. "Donnerstag, 15. Oktober" on the
     * German site and the English form on /en.
     */
    updateDeliveryDate() {
      const days = parseInt(this.getAttribute('data-delivery-days'), 10);
      if (!days || !window.Intl) return;

      // Lead time is in working days: Saturdays and Sundays do not count.
      const date = new Date();
      let counted = 0;
      while (counted < days) {
        date.setDate(date.getDate() + 1);
        const weekday = date.getDay();
        if (weekday !== 0 && weekday !== 6) counted += 1;
      }

      let label;
      try {
        label = new Intl.DateTimeFormat(this.getAttribute('data-delivery-locale') || undefined, {
          weekday: 'long',
          day: 'numeric',
          month: 'long'
        }).format(date);
      } catch (error) {
        return;
      }

      this.querySelectorAll('[data-delivery-date]').forEach((node) => {
        node.textContent = label;
      });

      const template = this.getAttribute('data-postcode-template');
      if (template) this.setAttribute('data-postcode-success', template.replace('[date]', label));
    }

    onCheck(event) {
      event.preventDefault();
      if (!this.postcodeResult) return;

      const value = (this.postcodeInput ? this.postcodeInput.value : '').trim();
      const pattern = this.getAttribute('data-postcode-pattern') || '^\\d{4,5}$';
      const valid = new RegExp(pattern).test(value);

      this.postcodeResult.textContent = valid
        ? this.getAttribute('data-postcode-success') || ''
        : this.getAttribute('data-postcode-error') || '';
      this.postcodeResult.hidden = false;

      if (this.postcodeInput) {
        this.postcodeInput.setAttribute('aria-invalid', valid ? 'false' : 'true');
      }
    }

    lockScroll() {
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (this.previousOverflow === undefined) return;
      document.body.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
    }
  }

  customElements.define('product-drawers', ProductDrawers);

  class ProductVariants extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('data-section-id');
      this.onClick = this.onClick.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('popstate', this.onPopState);
      if (this.controller) this.controller.abort();
    }

    onClick(event) {
      const link = event.target.closest('[data-variant-link]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      if (link.hasAttribute('aria-current')) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      const href = link.getAttribute('href');
      window.history.pushState({ variant: true }, '', href);
      this.render(href);
    }

    onPopState() {
      this.render(window.location.href);
    }

    render(href) {
      const url = new URL(href, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      this.setAttribute('aria-busy', 'true');

      fetch(url.toString(), { signal: this.controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const fresh = html.querySelector('product-variants');
          if (!fresh) throw new Error('missing product-variants');

          this.innerHTML = fresh.innerHTML;
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          window.location.href = href;
        })
        .finally(() => {
          this.removeAttribute('aria-busy');
        });
    }
  }

  if (!customElements.get('product-variants')) {
    customElements.define('product-variants', ProductVariants);
  }

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.product-editorial') || document;
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-slide]'));
      this.counter = this.querySelector('[data-gallery-counter]');
      this.index = 0;
      this.opener = null;

      this.onRootClick = this.onRootClick.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.root.addEventListener('click', this.onRootClick);
      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      this.root.removeEventListener('click', this.onRootClick);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeydown);
      this.unlockScroll();
    }

    onRootClick(event) {
      const opener = event.target.closest('[data-gallery-open]');
      if (!opener || this.contains(opener)) return;

      event.preventDefault();
      this.opener = opener;

      const index = parseInt(opener.getAttribute('data-gallery-open'), 10);
      this.open(isNaN(index) ? 0 : index);
    }

    onClick(event) {
      if (event.target.closest('[data-gallery-close]')) {
        this.close();
      } else if (event.target.closest('[data-gallery-prev]')) {
        this.step(-1);
      } else if (event.target.closest('[data-gallery-next]')) {
        this.step(1);
      }
    }

    onKeydown(event) {
      if (this.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'ArrowLeft') {
        this.step(-1);
      } else if (event.key === 'ArrowRight') {
        this.step(1);
      }
    }

    open(index) {
      this.show(index);
      this.hidden = false;
      this.lockScroll();

      const close = this.querySelector('[data-gallery-close]');
      if (close) close.focus();
    }

    close() {
      this.hidden = true;
      this.unlockScroll();

      if (this.opener) {
        this.opener.focus();
        this.opener = null;
      }
    }

    step(delta) {
      if (this.slides.length < 2) return;
      this.show((this.index + delta + this.slides.length) % this.slides.length);
    }

    show(index) {
      if (!this.slides.length) return;

      this.index = Math.min(Math.max(index, 0), this.slides.length - 1);
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== this.index;
      });

      if (this.counter) {
        this.counter.textContent = this.index + 1 + ' / ' + this.slides.length;
      }
    }

    lockScroll() {
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (this.previousOverflow === undefined) return;
      document.body.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
    }
  }

  if (!customElements.get('product-gallery')) {
    customElements.define('product-gallery', ProductGallery);
  }

  class ProductBar extends HTMLElement {
    connectedCallback() {
      const root = this.closest('.product-editorial');
      this.anchor = root ? root.querySelector('.product-editorial__button') : null;

      // No anchor to watch, or no observer: leave the bar out rather than
      // pinning it over the page for the whole visit.
      if (!this.anchor || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const notYetReached = entry.boundingClientRect.top > 0;
            this.hidden = entry.isIntersecting || notYetReached;
          });
        },
        { threshold: 0 }
      );

      this.observer.observe(this.anchor);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  if (!customElements.get('product-bar')) {
    customElements.define('product-bar', ProductBar);
  }
})();
